import { useState } from 'react';
import type { ReactNode } from 'react';
import { useLive, useRuntime } from './context';
import { taskFor } from '../outbox/tasks';
import type { OwnerTask } from '../outbox/tasks';
import { formatClock } from '../mirror/time';
import { BookSheet } from './BookSheet';
import type { BookSheetPrefill } from './BookSheet';
import { sourceWord } from './Today';

/**
 * Tasks — spec §8.3, on screen.
 *
 * > The server wins. A queued intent that no longer applies — the slot was taken
 * > by the partner while the iPad was offline — comes back rejected, and the PWA
 * > raises it as a task for the owner: *"This 2 PM was booked by the concierge
 * > while you were offline. Reschedule Dana?"*
 *
 * This screen is the honest cost of the model made visible. It is deliberately
 * not an error log: every card names a person, a time and what happened to it,
 * and offers the two things there are to do — book them somewhere else, or let
 * it go. There is no "retry" on a conflict, because retrying is exactly the
 * thing that cannot work: the room is occupied, and pretending otherwise is how
 * a device creates a booking the partner has already sold.
 *
 * Dismiss deletes the intent. It does not un-reject it and it does not write
 * anything to the cloud — the cloud already knows what it decided.
 */
export function Tasks(): ReactNode {
  const runtime = useRuntime();
  const [rebooking, setRebooking] = useState<BookSheetPrefill | null>(null);

  const session = useLive(() => runtime.mirror.session(), null);
  const timezone = session?.tenant.timezone ?? 'UTC';
  const rejected = useLive(() => runtime.outbox.rejected(), []);
  const tasks: OwnerTask[] = rejected.map((entry) => taskFor(entry, { timezone }));

  if (tasks.length === 0) {
    return (
      <section className="view">
        <h1>Nothing needs you</h1>
        <p className="empty">
          Everything this iPad has been told has landed in the cloud. Anything that collides while
          you are offline will show up here.
        </p>
      </section>
    );
  }

  return (
    <section className="view">
      <h1>
        {tasks.length} thing{tasks.length === 1 ? '' : 's'} need
        {tasks.length === 1 ? 's' : ''} you
      </h1>

      <ul className="task-list">
        {tasks.map((task) => (
          <li key={task.id} className="task">
            <h2>{task.title}</h2>
            <p className="task__message">{task.message}</p>

            {task.conflicts.length > 0 ? (
              <ul className="task__conflicts">
                {task.conflicts.map((conflict) => (
                  <li key={conflict.appointment_id}>
                    <span className={`badge badge--source-${conflict.source}`}>
                      {sourceWord(conflict.source)}
                    </span>
                    <span>
                      {formatClock(conflict.start, timezone)} –{' '}
                      {formatClock(conflict.end, timezone)}
                      {conflict.client_name === null ? '' : ` · ${conflict.client_name}`}
                      {conflict.service_name === null ? '' : ` · ${conflict.service_name}`}
                    </span>
                  </li>
                ))}
              </ul>
            ) : null}

            <div className="task__actions">
              {task.canReschedule ? (
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() =>
                    setRebooking({
                      serviceId: task.prefill?.serviceId,
                      resourceId: task.prefill?.resourceId,
                      clientId: task.prefill?.clientId,
                      guestName: task.prefill?.guestName,
                      startIso: task.prefill?.startIso,
                      source: 'staff',
                    })
                  }
                >
                  Find another time
                </button>
              ) : null}
              <button
                type="button"
                className="button button--ghost"
                onClick={() => void runtime.outbox.dismiss(task.id)}
              >
                Dismiss
              </button>
            </div>
          </li>
        ))}
      </ul>

      {rebooking !== null ? (
        <BookSheet
          prefill={rebooking}
          onDone={() => setRebooking(null)}
          onCancel={() => setRebooking(null)}
        />
      ) : null}
    </section>
  );
}
